import { getGradeLabel } from '@car-pool/shared/schoolRecords';

export const FALLBACK_GRADE_CODES = [-3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function parseIntegerList(value, { positiveOnly = false } = {}) {
  if (value === undefined || value === null || value === '') return [];

  let source = value;
  if (typeof value === 'string') {
    try {
      source = JSON.parse(value);
    } catch {
      source = value.split(',');
    }
  }

  const values = Array.isArray(source) ? source : [source];
  return [...new Set(values
    .map(Number)
    .filter((item) => Number.isInteger(item) && (!positiveOnly || item > 0)))]
    .sort((left, right) => left - right);
}

export function togglePushTarget(values, target) {
  const normalizedTarget = Number(target);
  const next = new Set(parseIntegerList(values));
  if (next.has(normalizedTarget)) next.delete(normalizedTarget);
  else next.add(normalizedTarget);
  return [...next].sort((left, right) => left - right);
}

export function buildPushAudiencePayload({
  audience = 'all',
  targetGrades = [],
  targetGradeSplitIds = [],
} = {}) {
  const payload = { audience };
  if (audience !== 'parents' || (!targetGrades.length && !targetGradeSplitIds.length)) {
    return payload;
  }

  return {
    ...payload,
    target_grades: targetGrades,
    target_grade_split_ids: targetGradeSplitIds,
  };
}

export function buildPushGradeOptions(gradeNames = []) {
  const activeGrades = gradeNames
    .filter((grade) => grade.is_active === true || Number(grade.is_active) === 1)
    .map((grade) => Number(grade.grade_code))
    .filter(Number.isInteger);
  const gradeCodes = activeGrades.length ? activeGrades : FALLBACK_GRADE_CODES;
  const labelMap = Object.fromEntries(
    gradeNames.map((grade) => [Number(grade.grade_code), grade.label]).filter(([, label]) => label)
  );

  return [...new Set(gradeCodes)].sort((left, right) => left - right).map((gradeCode) => ({
    gradeCode,
    label: getGradeLabel(gradeCode, labelMap, { short: true }),
  }));
}

export function buildPushGradeSplitOptions(gradeSplits = [], gradeNames = []) {
  const labelMap = Object.fromEntries(
    gradeNames.map((grade) => [Number(grade.grade_code), grade.label]).filter(([, label]) => label)
  );

  return gradeSplits
    .filter((split) => split.is_active === true || Number(split.is_active) === 1)
    .map((split) => ({
      splitId: Number(split.id),
      label: `${getGradeLabel(split.grade, labelMap, { short: true })} - ${split.split_code}`,
    }))
    .filter((split) => Number.isInteger(split.splitId) && split.splitId > 0)
    .sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true }));
}

export function formatPushTargetSummary(campaign, gradeOptions = [], gradeSplitOptions = []) {
  const gradeLabels = new Map(gradeOptions.map((grade) => [grade.gradeCode, grade.label]));
  const splitLabels = new Map(gradeSplitOptions.map((split) => [split.splitId, split.label]));
  const grades = parseIntegerList(campaign?.target_grades);
  const splitIds = parseIntegerList(campaign?.target_grade_split_ids, { positiveOnly: true });
  const parts = [];

  if (grades.length) {
    parts.push(`Grades: ${grades.map((grade) => gradeLabels.get(grade) || getGradeLabel(grade, {}, { short: true })).join(', ')}`);
  }
  if (splitIds.length) {
    parts.push(`Splits: ${splitIds.map((splitId) => splitLabels.get(splitId) || `#${splitId}`).join(', ')}`);
  }

  return parts.join(' · ');
}
